import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { formatDateTime } from '../../../utils/Utils';
import { ownerIdsOf } from '../../ats-requisitions/requisitionActions';
import {
  getNotes, createNote, updateNote, deleteNote, NOTE_BODY_MAX,
} from '../config/profileApi';

// NOTLAR — kişiye (LifecyclePersonId) bağlı; adayın bölünmüş satırları ortak görür.
//
// YAZAR KURALI (SPEC F1, sunucunun aynası):
//   • DÜZENLEME yalnız notun YAZARINA açıktır — AT.Write/admin bile başkasının
//     notunu düzenleyemez (not, yazanın ifadesidir).
//   • SİLME: yazar kendi notunu; AT.Write/admin herkesinkini.
//   • Sistem notları (rıza yenileme izi vb., yazarsız) düzenlenmez.
// Yazar karşılaştırması kimlik AİLESİYLE yapılır (User.Id + EmployeeId — bir kişinin
// iki Users satırı olabilir); sunucu `isMine` bayrağı gönderirse o önceliklidir.

const Body = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 640px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export const isNoteAuthor = (note, ownerIds) => {
  if (!note) return false;
  if (typeof note.isMine === 'boolean') return note.isMine;
  return !!note.authorUserId && ownerIds.includes(note.authorUserId);
};

export const canEditNote = (note, ownerIds) =>
  !!note && !note.isSystem && isNoteAuthor(note, ownerIds);

export const canDeleteNote = (note, ownerIds, writable) =>
  !!note && (!!writable || (!note.isSystem && isNoteAuthor(note, ownerIds)));

export default function CandidateNotesTab({ candidateId, writable, readOnly = false }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuth() || {};
  const ownerIds = useMemo(() => ownerIdsOf(user), [user]);

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notes, setNotes] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [drawer, setDrawer] = useState({ open: false, note: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, note: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    setFetching(true);
    getNotes(candidateId)
      .then(({ ok, items }) => {
        if (!alive) return;
        setLoadFailed(!ok);
        setNotes(items);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [candidateId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleSave = useCallback(async (values) => {
    const body = (values.body || '').trim();
    if (!body) return false;
    const res = drawer.note
      ? await updateNote(candidateId, drawer.note.id, body)
      : await createNote(candidateId, body);
    if (!res.ok) return false;
    setDrawer({ open: false, note: null });
    reload();
    return true;
  }, [candidateId, drawer.note, reload]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.note) return;
    setDeleting(true);
    const res = await deleteNote(candidateId, deleteDialog.note.id);
    setDeleting(false);
    setDeleteDialog({ isOpen: false, note: null });
    if (res.ok) reload();
  }, [candidateId, deleteDialog.note, reload]);

  const buildActions = useCallback((note) => {
    if (readOnly) return [];
    const actions = [];
    if (canEditNote(note, ownerIds)) {
      actions.push({ type: 'edit', label: t('common.edit'), onClick: () => setDrawer({ open: true, note }) });
    }
    if (canDeleteNote(note, ownerIds, writable)) {
      if (actions.length > 0) actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, note }),
      });
    }
    return actions;
  }, [t, ownerIds, writable, readOnly]);

  const columns = useMemo(() => [
    {
      name: 'body',
      label: t('ats.candidateProfile.note'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const note = notes[tableMeta.rowIndex];
          return (
            <Body>
              {v}
              {note?.isSystem && <Muted>{` · ${t('ats.candidateProfile.systemNote')}`}</Muted>}
            </Body>
          );
        },
      },
    },
    {
      name: 'authorName',
      label: t('ats.candidateProfile.noteAuthor'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'createDate',
      label: t('ats.candidateProfile.noteDate'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const note = notes[tableMeta.rowIndex];
          return (
            <span>
              {formatDateTime(v, lang)}
              {note?.updateDate && <Muted>{` · ${t('ats.candidateProfile.edited')}`}</Muted>}
            </span>
          );
        },
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const note = notes[tableMeta.rowIndex];
          const actions = buildActions(note);
          if (actions.length === 0) return null;
          return <OptionsMenu actions={actions} row={note} />;
        },
      },
    },
  ], [t, lang, notes, buildActions]);

  const schema = useMemo(() => [
    {
      name: 'body',
      label: t('ats.candidateProfile.note'),
      type: 'textarea',
      rows: 6,
      grid: 12,
      required: true,
      description: t('ats.candidateProfile.noteMaxHint', { max: NOTE_BODY_MAX }),
    },
  ], [t]);

  // SABİT referans: GenericFormDrawer `initialValues` değişince formu sıfırlar.
  const initialValues = useMemo(
    () => (drawer.note ? { body: drawer.note.body || '' } : null),
    [drawer.note],
  );

  return (
    <div data-role="candidate-notes">
      {loadFailed && <LoadErrorBanner message={t('ats.candidateProfile.notesLoadFailed')} />}

      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, note: null })}
        title={drawer.note ? t('ats.candidateProfile.editNote') : t('ats.candidateProfile.newNote')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.candidateProfile.deleteNoteConfirm')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, note: null })}
        loading={deleting}
      />

      <DataTable
        title={t('ats.candidateProfile.notesTitle')}
        fetching={fetching}
        columns={columns}
        data={notes}
        totalRowCount={notes.length}
        serverSide={false}
        disableFilters
        canCreateNewRecord={!readOnly}
        newButtonLabel={t('ats.candidateProfile.newNote')}
        onNewButtonClick={() => setDrawer({ open: true, note: null })}
      />
    </div>
  );
}
